import {
  Banner,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  FileButton,
  Input,
  SettingsRow,
  SettingsSection,
  SettingsSectionSkeleton,
  SignaturePad,
  toast,
  useDraft,
} from '@nexus/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { ApiError, api, inputErrors } from '../api/client';
import { useModules } from '../modules/context';
import { canIn, useSession } from '../session/session';
import { SIGNATURE_UPLOAD_TYPES, SignatureUploadError, signatureFromFile } from './upload';

/** Characters of the job title, as the API allows (core-server signatures.ts). */
const ROLE_MAX = 120;

/** The own signature, the image as PNG data URL. */
interface OwnSignature {
  role: string | null;
  image: string | null;
  version: number;
}

/** A save replaces both fields; `version` is missing for the first signature. */
interface SignatureInput {
  role: string | null;
  image: string | null;
  version?: number;
}

export const ownSignatureQuery = queryOptions({
  queryKey: ['signature', 'me'],
  queryFn: ({ signal }) => api.get<OwnSignature | null>('/me/signature', { signal }),
});

/** Toast for a failed change; 403 names when a signature may be stored at all. */
function useSaveError(): (error: unknown) => void {
  const { t } = useTranslation();
  const reportError = useAdminError();
  return useCallback(
    (error) => {
      if (error instanceof ApiError && error.status === 403) {
        toast.error(t('signature.errors.forbidden'));
      } else reportError(error);
    },
    [reportError, t],
  );
}

/**
 * Profile section "Unterschrift" (docs/architecture/signatures.md): draw or upload the own
 * signature, the job title under it, delete both. Shown to people who may create a document of
 * a module, and to everyone with a stored signature, so they can delete it.
 */
export function SignatureSection() {
  const { t } = useTranslation();
  const session = useSession();
  const modules = useModules();
  const client = useQueryClient();
  const reportError = useAdminError();
  const reportSaveError = useSaveError();
  const roleId = useId();
  const signature = useQuery(ownSignatureQuery);
  const [drawing, setDrawing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // As on the server: without a document there is no purpose for a signature.
  const mayKeep = modules.some((m) =>
    (m.manifest.documents ?? []).some((document) => canIn(session, document.permission)),
  );

  const save = useMutation({
    mutationFn: (input: SignatureInput) =>
      api.put<{ role: string | null; version: number }>('/me/signature', input),
    onSuccess: (answer, input) => {
      const saved: OwnSignature = {
        role: answer.role,
        image: input.image,
        version: answer.version,
      };
      client.setQueryData(ownSignatureQuery.queryKey, saved);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.message === 'version-conflict') {
        await client.invalidateQueries(ownSignatureQuery);
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete('/me/signature'),
    onSuccess: () => {
      client.setQueryData(ownSignatureQuery.queryKey, null);
      toast.success(t('signature.deleted'));
    },
    onError: async (error) => {
      reportError(error);
      await client.invalidateQueries(ownSignatureQuery);
    },
  });

  const current = signature.data ?? null;
  // While a save runs, the field shows what is being saved, not the old value.
  const shown = save.isPending ? save.variables : current;
  const role = useDraft(shown?.role ?? '', (text) =>
    save.mutate(
      { role: text.trim() || null, image: current?.image ?? null, version: current?.version },
      { onSuccess: () => toast.success(t('signature.roleSaved')), onError: reportSaveError },
    ),
  );

  if (!mayKeep && !current) return null;
  if (signature.isPending) return <SettingsSectionSkeleton rows={2} />;

  return (
    <>
      <SettingsSection
        title={t('signature.title')}
        description={mayKeep ? t('signature.description') : t('signature.unused')}
      >
        {signature.isError ? (
          <p className="py-4 text-fg-muted">{t('common.loadFailed')}</p>
        ) : (
          <>
            <SettingsRow
              label={t('signature.current')}
              description={
                current?.image ? (
                  <span className="mt-2 block max-w-xs rounded-md border border-line bg-paper p-2">
                    <img
                      src={current.image}
                      alt={t('signature.preview')}
                      className="block aspect-[3/1] w-full object-contain"
                    />
                  </span>
                ) : (
                  t('signature.none')
                )
              }
            >
              {mayKeep && (
                <Button icon="pencil" onClick={() => setDrawing(true)}>
                  {current?.image ? t('signature.change') : t('signature.add')}
                </Button>
              )}
              {current && (
                <Button variant="ghost" icon="trash-2" onClick={() => setDeleting(true)}>
                  {t('signature.delete')}
                </Button>
              )}
            </SettingsRow>
            <SettingsRow
              label={t('signature.role')}
              description={t('signature.roleHint')}
              htmlFor={roleId}
            >
              <Input
                id={roleId}
                {...role}
                maxLength={ROLE_MAX}
                autoComplete="organization-title"
                disabled={!mayKeep}
                className="md:w-56"
              />
            </SettingsRow>
          </>
        )}
      </SettingsSection>
      <SignatureDialog
        open={drawing}
        onOpenChange={setDrawing}
        saving={save.isPending}
        onSave={async (image) => {
          await save.mutateAsync({
            role: current?.role ?? null,
            image,
            version: current?.version,
          });
          toast.success(t('signature.saved'));
        }}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        tone="danger"
        title={t('signature.deleteTitle')}
        description={t('signature.deleteDescription')}
        confirmLabel={t('signature.delete')}
        onConfirm={() => remove.mutateAsync()}
      />
    </>
  );
}

/**
 * Drawing on the signature pad, or an uploaded picture instead: uploading is the way without
 * drawing by hand (WCAG 2.5.7).
 */
function SignatureDialog({
  open,
  onOpenChange,
  saving,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  /** Rejects with the error of the API. */
  onSave: (image: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const reportSaveError = useSaveError();
  const hintId = useId();
  const uploadButton = useRef<HTMLButtonElement>(null);
  const [drawn, setDrawn] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string>();
  const [shown, setShown] = useState(open);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setDrawn(null);
      setUploaded(null);
      setError(undefined);
    }
  }
  const image = uploaded ?? drawn;

  async function choose(file: File) {
    setReading(true);
    setError(undefined);
    try {
      setUploaded(await signatureFromFile(file));
      setDrawn(null);
    } catch (failure) {
      const problem = failure instanceof SignatureUploadError ? failure.problem : 'decode';
      setError(t(`signature.upload.${problem}`));
    } finally {
      setReading(false);
    }
  }

  function drawInstead() {
    setUploaded(null);
    setError(undefined);
    uploadButton.current?.focus();
  }

  async function submit() {
    if (!image) {
      setError(t('signature.dialog.missing'));
      return;
    }
    try {
      await onSave(image);
      onOpenChange(false);
    } catch (failure) {
      const field = inputErrors(failure, 'admin.errors.invalid-input')?.image;
      if (field) setError(t(field));
      else reportSaveError(failure);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('signature.dialog.title')}
        description={t('signature.dialog.description')}
        size="lg"
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={reading}
              onClick={() => void submit()}
            >
              {t('signature.dialog.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {uploaded ? (
            <div className="flex flex-col gap-2">
              <div className="rounded-md border border-line bg-paper p-2">
                <img
                  src={uploaded}
                  alt={t('signature.dialog.uploaded')}
                  className="block aspect-[3/1] w-full object-contain"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon="pencil"
                className="self-end"
                onClick={drawInstead}
              >
                {t('signature.dialog.draw')}
              </Button>
            </div>
          ) : (
            <SignaturePad
              label={t('signature.dialog.pad')}
              clearLabel={t('signature.dialog.clear')}
              onChange={(next) => {
                setDrawn(next);
                if (next) setError(undefined);
              }}
            />
          )}
          <div className="flex flex-col items-start gap-1.5">
            <FileButton
              ref={uploadButton}
              accept={SIGNATURE_UPLOAD_TYPES.join(',')}
              icon="upload"
              loading={reading}
              aria-describedby={hintId}
              onFile={(file) => void choose(file)}
            >
              {t('signature.dialog.upload')}
            </FileButton>
            <p id={hintId} className="text-xs text-fg-muted">
              {t('signature.dialog.uploadHint')}
            </p>
          </div>
          {error && (
            <Banner tone="danger" role="alert">
              {error}
            </Banner>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
